import {
  CompletedCycleCount,
  CycleCountSessionItem,
} from '../models/cycle-count-session.model';
import { formatVarianceReportDate } from './cycle-count-session.utils';

/** DB-constrained cycle-count size range (matches the CycleCount.CountSize CHECK, 10–200). */
export const CYCLE_COUNT_MIN_SIZE = 10;
export const CYCLE_COUNT_MAX_SIZE = 200;

/**
 * #34106: the cap a single material contributes toward the count size. A material in N sublocations (bins)
 * counts as at most 2 toward "item count". Mirrors the server constant
 * CycleCountSelectionUtils.MaxItemContributionPerMaterial (C#); keep the two in lockstep.
 */
export const MAX_ITEM_CONTRIBUTION_PER_MATERIAL = 2;

/**
 * #34106: the count-size unit. Session items are stored one row per sublocation (bin), and a material in N
 * bins contributes min(N, 2) toward the count size, so the session's item count is the sum of min(binCount, 2)
 * over its distinct materials, NOT the raw row count. This mirrors the server's grouping in
 * CycleCountConfigRepository.AddItems, so the growth pre-flight validates against the same budget the API uses.
 */
export function computeCappedItemCount(
  items: readonly CycleCountSessionItem[]
): number {
  if (!items || items.length === 0) return 0;
  const binsByMaterial = new Map<string, number>();
  for (const item of items) {
    binsByMaterial.set(
      item.materialId,
      (binsByMaterial.get(item.materialId) ?? 0) + 1
    );
  }
  let total = 0;
  for (const bins of binsByMaterial.values()) {
    total += Math.min(bins, MAX_ITEM_CONTRIBUTION_PER_MATERIAL);
  }
  return total;
}

/**
 * Builds the user-facing message after growing a count (#30580). Because add-items appends only the
 * items that actually exist, a warehouse can run out of uncounted stock before the requested size is
 * reached (e.g. a +5 grow on a small warehouse that only had 4 uncounted items left). This message makes
 * that visible instead of a bland "Added 4 items" that reads as a dropped item:
 * - counts unavailable → a generic confirmation (never renders "undefined");
 * - nothing added → says the warehouse has no more uncounted items;
 * - fewer added than requested → reports what was added, the target, and that the warehouse ran out;
 * - fully satisfied → a plain success confirmation.
 *
 * The count fields are typed loosely (`number | null | undefined`) on purpose: the caller passes the API
 * response through, and a mis-cased or partial response body must degrade gracefully rather than print
 * "undefined items".
 */
export function buildAddItemsResultMessage(
  response: {
    addedCount?: number | null;
    totalItems?: number | null;
  },
  requestedNewSize: number,
  warehouseLabel?: string
): string {
  const where = warehouseLabel?.trim()
    ? warehouseLabel.trim()
    : 'this warehouse';
  const items = (n: number): string => `${n} item${n === 1 ? '' : 's'}`;

  const addedCount = response?.addedCount;
  const totalItems = response?.totalItems;

  // Defensive: if the response did not carry the counts, confirm the add without inventing numbers.
  if (!Number.isFinite(addedCount as number)) {
    return `Items were added to the cycle count for ${where}. Refresh to see the updated count.`;
  }

  const added = addedCount as number;
  if (added <= 0) {
    return `No items were added — ${where} has no more uncounted items available.`;
  }

  if (
    Number.isFinite(totalItems as number) &&
    (totalItems as number) < requestedNewSize
  ) {
    return (
      `Added ${items(added)} to the cycle count for ${where}. ` +
      `It has no more uncounted items, so the count reached ${totalItems} ` +
      `of the requested ${requestedNewSize}.`
    );
  }

  return `Added ${items(added)} to the cycle count for ${where}.`;
}

/**
 * Validates a request to grow an open count to a new size (#30580). The new size must be a whole number
 * within the DB range (10–200) and strictly greater than the current item count — growing must actually add
 * items, so a no-op or shrink is rejected. Returns an error message, or null when acceptable.
 */
export function validateGrowCountSize(
  currentItemCount: number,
  newCountSize: number
): string | null {
  if (
    newCountSize == null ||
    Number.isNaN(newCountSize) ||
    !Number.isInteger(newCountSize)
  ) {
    return 'Enter a whole number for the new count size.';
  }
  if (
    newCountSize < CYCLE_COUNT_MIN_SIZE ||
    newCountSize > CYCLE_COUNT_MAX_SIZE
  ) {
    return `Count size must be between ${CYCLE_COUNT_MIN_SIZE} and ${CYCLE_COUNT_MAX_SIZE}.`;
  }
  if (newCountSize <= currentItemCount) {
    return `New count size must be greater than the current ${currentItemCount} items.`;
  }
  return null;
}

/**
 * Picks the default entry for the detail page's variance-report dropdown (#30580): the most recent completed
 * count. A count with no completed date never wins over one with a real date. Returns null for an empty list.
 * Does not mutate the input.
 */
export function selectDefaultCompletedCount(
  counts: CompletedCycleCount[]
): CompletedCycleCount | null {
  if (!counts || counts.length === 0) return null;

  return counts.reduce((latest, current) =>
    completedTime(current) > completedTime(latest) ? current : latest
  );
}

function completedTime(count: CompletedCycleCount): number {
  if (!count.completedDate) return 0;
  const time = new Date(count.completedDate).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Builds the option label for a completed count in the variance-report picker (#30580), e.g.
 * "CC-2026-0817 · 08/17/2026 (Complete)". Mirrors the web detail-page dropdown so the mobile
 * action-sheet picker reads identically. A missing completed date renders as an em dash.
 */
export function buildCompletedCountLabel(count: CompletedCycleCount): string {
  const date = count.completedDate
    ? formatVarianceReportDate(count.completedDate)
    : '—';
  return `${count.displayId} · ${date} (${count.status})`;
}
